import { GCSEOHead, CTABanner, Breadcrumbs, RelatedGuides, LastUpdated } from "./GCLayout";
import { gcCanonicalBase } from "./gcDomain";
import { JsonLd } from "../../components/JsonLd";
import { ArrowRight, Users, Briefcase, Heart, Award, Globe, Shuffle } from "lucide-react";

const CATEGORIES = [
  {
    icon: Users,
    title: "Family-Based Green Card",
    form: "I-130 / I-485",
    who: "Spouses, children, parents, and siblings of U.S. citizens or permanent residents.",
    steps: [
      "U.S. citizen or LPR files Form I-130 (Petition for Alien Relative)",
      "Wait for priority date to become current (immediate relatives skip this)",
      "File Form I-485 (Adjustment of Status) if in the U.S., or DS-260 via consular processing",
      "Attend biometrics appointment and interview",
      "Receive Green Card approval",
    ],
    timeline: "Immediate relatives: 12-18 months. Other categories: 2-10+ years depending on country and preference.",
  },
  {
    icon: Briefcase,
    title: "Employment-Based Green Card",
    form: "I-140 / I-485",
    who: "Workers with job offers, extraordinary ability individuals, and investors.",
    steps: [
      "Employer obtains PERM Labor Certification (most categories)",
      "Employer files Form I-140 (Immigrant Petition for Alien Workers)",
      "Wait for priority date to become current",
      "File Form I-485 or process at consulate abroad",
      "Attend interview and receive Green Card",
    ],
    timeline: "EB-1: 1-3 years. EB-2/EB-3: 2-10+ years (backlogged for India/China).",
  },
  {
    icon: Shuffle,
    title: "Diversity Visa Lottery (DV)",
    form: "DS-260",
    who: "Nationals of countries with low immigration rates to the U.S.",
    steps: [
      "Submit online entry at dvprogram.state.gov during registration period",
      "Check results on the Entrant Status Check",
      "If selected, submit DS-260 (Immigrant Visa Application)",
      "Gather documents and attend consular interview",
      "Enter the U.S. with immigrant visa and receive Green Card by mail",
    ],
    timeline: "Registration: October-November. Results: May. Interview: varies. Total: ~12-18 months if selected.",
  },
  {
    icon: Heart,
    title: "Humanitarian Green Card",
    form: "Varies",
    who: "Refugees, asylees, VAWA petitioners, and other humanitarian categories.",
    steps: [
      "Obtain refugee or asylee status (or other qualifying humanitarian status)",
      "After 1 year in the U.S., file Form I-485 to adjust to permanent resident status",
      "Attend biometrics and interview",
      "Receive Green Card",
    ],
    timeline: "Asylees/Refugees: 1-2 years after status grant. VAWA: 2-4 years.",
  },
  {
    icon: Award,
    title: "Special Immigrant Green Card",
    form: "I-360",
    who: "Religious workers, certain juveniles, Iraqi/Afghan translators, and other special categories.",
    steps: [
      "File Form I-360 (Petition for Amerasian, Widow(er), or Special Immigrant)",
      "Receive approval from USCIS",
      "File Form I-485 or process at consulate",
      "Attend interview and receive Green Card",
    ],
    timeline: "Typically 1-3 years depending on category.",
  },
  {
    icon: Globe,
    title: "Investment-Based (EB-5)",
    form: "I-526E / I-485",
    who: "Investors who invest $800,000-$1,050,000 in a U.S. business creating 10+ jobs.",
    steps: [
      "Make qualifying investment in a new commercial enterprise or regional center",
      "File Form I-526E (Immigrant Petition by Investor)",
      "After approval, file I-485 or process at consulate",
      "Receive conditional Green Card (2-year validity)",
      "File I-829 to remove conditions and get permanent Green Card",
    ],
    timeline: "3-5+ years from petition to permanent card.",
  },
];

const GUIDE_FAQS = [
  {
    q: "Which Green Card category is the fastest?",
    a: "Immediate relatives of U.S. citizens (spouses, parents, unmarried children under 21) have no annual cap and typically receive a Green Card in 12-18 months. EB-1A (Extraordinary Ability) is often the fastest employment-based route at 1-3 years.",
  },
  {
    q: "Can I switch between Green Card categories?",
    a: "In some cases, yes. For example, if your employer sponsors you under EB-3 but you later qualify for EB-2, you may be able to port your priority date. Changes depend on your specific situation and require careful planning.",
  },
  {
    q: "Do I need a sponsor for every Green Card category?",
    a: "Not always. Family-based and most employment-based categories require a petitioner (sponsor). However, EB-1A (Extraordinary Ability), EB-2 NIW (National Interest Waiver), and the DV Lottery allow you to self-petition without a sponsor.",
  },
  {
    q: "What is the difference between EB-2 and EB-3?",
    a: "EB-2 is for professionals with advanced degrees or exceptional ability. EB-3 is for skilled workers, professionals with bachelor's degrees, and some unskilled workers. EB-2 generally has shorter wait times but stricter qualification requirements.",
  },
];

export default function GCGuidePage() {
  const base = gcCanonicalBase();

  return (
    <>
      <GCSEOHead
        title="All 6 Green Card Categories Explained - 2026 Update"
        description="Comprehensive 2026 guide to all U.S. Green Card categories: family-based, employment-based, DV Lottery, humanitarian, special immigrant, and EB-5 investment. Step-by-step process for each."
        path="/green-card-guide"
      />

      {/* JSON-LD Article Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "All 6 Green Card Categories Explained - 2026 Update",
            description: "Comprehensive guide to every U.S. Green Card category with step-by-step instructions.",
            dateModified: "2026-02-01",
            url: `${base}/green-card-guide`,
            publisher: { "@type": "Organization", name: "ImmigrationGuide", url: "https://immigrationguide.us" },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${base}/green-card-guide` },
            articleSection: CATEGORIES.map((c) => c.title),
          }} />

      {/* JSON-LD FAQ Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: GUIDE_FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }} />

      {/* Breadcrumbs */}
      <Breadcrumbs current="Green Card Guide" />

      {/* Header */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-4" data-testid="gc-guide-h1">
            All 6 U.S. Green Card Categories, Explained
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
            A Green Card (Form I-551) is your path to permanent residency in the United States. There are six
            main ways to get one — each with different eligibility rules, forms, and wait times. Below is every
            major category with a step-by-step breakdown so you know exactly what to expect.
          </p>
          <div className="mt-4">
            <LastUpdated />
          </div>
        </div>
      </section>

      {/* Featured Snippet Box */}
      <section className="py-6 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-emerald-50 rounded-lg p-5 border border-emerald-100" data-testid="gc-guide-snippet">
            <h2 className="font-semibold text-slate-900 text-sm mb-2">How many Green Card categories are there?</h2>
            <p className="text-sm text-slate-700 leading-relaxed">
              There are <strong>6 main categories</strong>: Family-Based, Employment-Based, Diversity Visa Lottery,
              Humanitarian, Special Immigrant, and Investment-Based (EB-5). The fastest path is for immediate relatives
              of U.S. citizens (12-18 months). The longest waits are employment-based for applicants from India and China
              (5-10+ years).
            </p>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">On This Page</h2>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <a
                key={c.title}
                href={`#${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
              >
                {c.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 md:py-16" data-testid="gc-guide-categories">
        <div className="max-w-4xl mx-auto px-4 space-y-16">
          {CATEGORIES.map((c, i) => (
            <article
              key={c.title}
              id={c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
              className="scroll-mt-20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <c.icon className="h-5 w-5 text-emerald-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-slate-900 font-heading">
                  {i + 1}. {c.title}
                </h2>
              </div>

              <div className="ml-0 md:ml-13 space-y-4">
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">
                    Form: {c.form}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                    {c.timeline.split(".")[0]}
                  </span>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">Who qualifies?</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{c.who}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">Step-by-step process</h3>
                  <ol className="space-y-2">
                    {c.steps.map((s, j) => (
                      <li key={j} className="flex gap-3 text-sm text-slate-600">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold">
                          {j + 1}
                        </span>
                        <span className="pt-0.5">{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 text-sm">
                  <span className="font-semibold text-slate-700">Estimated timeline: </span>
                  <span className="text-slate-600">{c.timeline}</span>
                </div>
              </div>

              {i < CATEGORIES.length - 1 && <hr className="mt-12 border-slate-100" />}
            </article>
          ))}
        </div>
      </section>

      {/* Soft CTA */}
      <section className="py-10 bg-slate-50 border-y border-slate-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-slate-600 mb-4 text-base">
            Not sure which category fits your situation?
          </p>
          <a
            href="https://immigrationguide.us/auth?mode=register"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors"
            data-testid="gc-guide-inline-cta"
          >
            Check your eligibility in plain English <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16" data-testid="gc-guide-faq">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 font-heading mb-6">
            Common Questions About Green Card Categories
          </h2>
          <div className="space-y-6">
            {GUIDE_FAQS.map((faq) => (
              <div key={faq.q}>
                <h3 className="font-semibold text-slate-900 text-sm mb-1">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DV Lottery Resources */}
      <section className="py-10 border-t border-slate-100" data-testid="gc-guide-dv-resources">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg font-bold text-slate-900 font-heading mb-4">DV Lottery Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href="https://immigrationguide.us/how-to-check-dv-lottery-results" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">How to Check DV Lottery Results</h3>
              <p className="text-xs text-slate-500">Official steps to verify your selection status</p>
            </a>
            <a href="https://immigrationguide.us/dv-lottery-eligibility" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">DV Lottery Eligibility</h3>
              <p className="text-xs text-slate-500">Country, education, and work experience requirements</p>
            </a>
          </div>
        </div>
      </section>

      <RelatedGuides currentPath="/green-card-guide" />
      <CTABanner />
    </>
  );
}
